import type { ApolloError } from '@apollo/client/errors';
import { getLog } from '@aurora/shared-utils/log';
import React, { useEffect, useRef } from 'react';
import {
  PaginationDirection,
  PaginationUpdateStrategy
} from '../../../../helpers/ui/PaginationHelper/PaginationHelper';
import { LoadingSize } from '../../../../types/enums';
import { LoadingVariant } from '../../Loading/enums';
import Loading from '../../Loading/Loading';
import type { LoadingVariantTypeAndProps } from '../../Loading/types';
import type { PagerInfiniteScrollProps } from '../types';
import { getScrollParent } from '../../../../helpers/ui/ScrollHelper/ScrollHelper';
import useIntersectionFix from '../../../useIntersectionFix';

const log = getLog(module);

/**
 * Logs the error from the loadPage or customLoadPage functions
 *
 * @param error the error returned by the Promise
 */
function handleError(error: ApolloError | unknown): void {
  log.error(error, 'Error loading page of results');
}

/**
 * The `infiniteScroll` variant for `Pager`. Uses the Intersection Observer API, setting an observer on a hidden div
 *
 * If troubleshooting pagination, ensure the following:
 *
 * 1) If the scrollable element that should trigger more results is *not* the document body, you are providing a reference
 * to the scrollable element via the rootReference prop
 *
 * 2) the page size you specify will guarantee that scrollbars exist for the root element or document body. In other words
 * use a larger page size to ensure that the observer element will be out of view. This may require setting a max height
 * on the root element
 *
 * @link https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API
 *
 * @author Jonathan Bridges
 */
const PagerInfiniteScroll: React.FC<React.PropsWithChildren<PagerInfiniteScrollProps>> = ({
  pageInfo,
  loadPage,
  customLoadPage,
  threshold = 0.1,
  rootMargin = '0px 0px 100px 0px',
  rootReference,
  loaderClassName,
  loaderSize = LoadingSize.SM
}) => {
  const finalLoadingVariant: LoadingVariantTypeAndProps = {
    type: LoadingVariant.DOT,
    props: {
      size: loaderSize,
      useFullHeight: false,
      ...(loaderClassName && { className: loaderClassName })
    }
  };

  const { hasNextPage } = pageInfo;

  const ref = useRef<HTMLDivElement>();

  function getRootElement() {
    if (rootReference?.current) {
      return rootReference.current;
    }
    return ref.current ? getScrollParent(ref.current, false) : null;
  }

  const root = getRootElement();

  const intersection = useIntersectionFix(ref, { root, rootMargin, threshold });

  useEffect(() => {
    if (intersection?.isIntersecting) {
      if (customLoadPage) {
        customLoadPage().catch(handleError);
      } else {
        const promiseWrapper = loadPage?.(
          PaginationUpdateStrategy.APPEND,
          PaginationDirection.NEXT
        );
        if (promiseWrapper) {
          promiseWrapper.catch(handleError);
        }
      }
    }
  }, [customLoadPage, intersection?.isIntersecting, loadPage]);

  if (hasNextPage) {
    return <Loading variant={finalLoadingVariant} ref={ref} />;
  }
};

export default PagerInfiniteScroll;
